import {
  BRAND_DISPLAY_NAME,
  BRAND_WORDMARK_ACCENT_SUFFIX,
} from '@streamlain/brand';
import type { CustomAppConfig } from '@streamlain/view-config';
import type { MouseEvent } from 'react';
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewComponentRenderProps,
} from '../../../types/view-component.types';
import { resolveCustomAppRuntimeHref } from '../runtime-href';
import * as styles from '../styles.css';
import { LanguageSwitcherComponent } from './language-switcher';

const MAX_RENDER_DEPTH = 3;

const configSchema = z.object({
  visible: z.boolean().default(true),
  showDraftRoutes: z.boolean().default(true),
  orientation: z.enum(['auto', 'header', 'sidebar']).default('auto'),
});

interface NavigationEntry {
  id: string;
  label: string;
  href: string;
  depth: number;
}

interface CustomAppNavigationBranding {
  displayName: string;
  logoUrl: string | null;
}

function CustomAppNavigationRenderer({
  config,
  mode,
  runtimeContext,
}: ViewComponentRenderProps) {
  const { showDraftRoutes, visible } = config as z.infer<typeof configSchema>;
  // Persisted components created before the visibility option have no field.
  // Only an explicit opt-out hides the topbar.
  if (visible === false) return null;
  const customAppConfig = readCustomAppConfig(runtimeContext);
  const branding = readCustomAppBranding(runtimeContext);
  const entries = customAppConfig
    ? listNavigationEntries(
        customAppConfig,
        showDraftRoutes,
        runtimeContext?.locale
      )
    : [];
  const pageShape =
    runtimeContext?.view?.['pageShape'] === 'sidebar' ? 'sidebar' : 'header';
  const isSidebar =
    config.orientation === 'sidebar' ||
    (config.orientation !== 'header' && pageShape === 'sidebar');
  const isBuilder = mode === 'builder';
  const availableLocales = customAppConfig?.availableLocales ?? [];
  const showLanguageSwitcher =
    availableLocales.filter(
      (locale, index, locales) => locales.indexOf(locale) === index
    ).length > 1;

  return createElement(
    'header',
    {
      className: styles.customAppNavigation,
      'data-sl-role': 'custom-app-navigation',
      'data-sl-orientation': isSidebar ? 'sidebar' : 'header',
    },
    createBrandLink({ branding, isBuilder, isSidebar, runtimeContext }),
    createElement(
      'div',
      {
        className: styles.customAppNavigationActions,
      },
      createElement(
        'nav',
        {
          className: styles.customAppNavigationLinks,
          'data-sl-role': 'navigation-links',
          'aria-label': 'Custom app navigation',
        },
        ...entries.map(entry =>
          createElement(
            'a',
            {
              key: entry.id,
              // Preview keeps the real href and lets its route owner intercept
              // the bubbling click. Builder canvas remains non-navigable.
              href: resolveCustomAppRuntimeHref(entry.href, runtimeContext),
              onClick: (event: MouseEvent<HTMLAnchorElement>) => {
                if (isBuilder) event.preventDefault();
              },
              className: styles.customAppNavigationLink,
              'data-sl-role': 'nav-link',
              style: {
                marginLeft: isSidebar ? Math.min(entry.depth, 2) * 12 : 0,
                fontWeight: entry.depth === 0 ? 600 : 500,
              },
            },
            !isSidebar && entry.depth > 0 ? `> ${entry.label}` : entry.label
          )
        )
      ),
      showLanguageSwitcher
        ? LanguageSwitcherComponent.render({
            config: LanguageSwitcherComponent.defaultConfig,
            data: [],
            mode,
            runtimeContext,
          })
        : null
    )
  );
}

function createBrandLink(input: {
  branding: CustomAppNavigationBranding;
  isBuilder: boolean;
  isSidebar: boolean;
  runtimeContext?: ViewComponentRenderProps['runtimeContext'];
}) {
  const { branding, isBuilder, isSidebar, runtimeContext } = input;
  return createElement(
    'a',
    {
      key: 'custom-app-brand-home',
      href: resolveCustomAppRuntimeHref('/', runtimeContext),
      onClick: (event: MouseEvent<HTMLAnchorElement>) => {
        if (isBuilder) event.preventDefault();
      },
      className: styles.customAppNavigationBrand,
      'data-sl-role': 'brand',
      style: { marginBottom: isSidebar ? 4 : 0 },
    },
    createBrandLogo(branding),
    createElement(
      'span',
      { className: styles.customAppNavigationBrandText },
      createBrandText(branding.displayName)
    )
  );
}

function createBrandText(displayName: string) {
  const wordmark = BRAND_DISPLAY_NAME;
  if (displayName !== wordmark && !displayName.startsWith(`${wordmark} `)) {
    return displayName;
  }
  const suffix = displayName.slice(wordmark.length);
  if (!BRAND_WORDMARK_ACCENT_SUFFIX) {
    return `${wordmark}${suffix}`;
  }
  return createElement(
    'span',
    null,
    wordmark.slice(0, -BRAND_WORDMARK_ACCENT_SUFFIX.length),
    createElement(
      'span',
      {
        className: styles.customAppNavigationBrandAccent,
        'data-sl-role': 'brand-accent',
      },
      BRAND_WORDMARK_ACCENT_SUFFIX
    ),
    suffix
  );
}

function createBrandLogo(branding: CustomAppNavigationBranding) {
  return branding.logoUrl
    ? createElement('img', {
        src: branding.logoUrl,
        alt: branding.displayName,
        className: styles.customAppNavigationLogo,
      })
    : createElement(
        'span',
        {
          className: styles.customAppNavigationLogoFallback,
          'aria-label': branding.displayName,
          role: 'img',
        },
        brandInitial(branding.displayName)
      );
}

function brandInitial(displayName: string) {
  return (
    displayName.trim().charAt(0).toLocaleUpperCase() ||
    BRAND_DISPLAY_NAME.charAt(0).toLocaleUpperCase()
  );
}

function readCustomAppConfig(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): CustomAppConfig | null {
  const customApp = runtimeContext?.customApp;
  const raw =
    customApp && typeof customApp === 'object' && 'config' in customApp
      ? (customApp as { config?: unknown }).config
      : null;
  return raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as CustomAppConfig).pages) &&
    Array.isArray((raw as CustomAppConfig).navigation)
    ? (raw as CustomAppConfig)
    : null;
}

function readCustomAppBranding(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): CustomAppNavigationBranding {
  const customApp = runtimeContext?.customApp;
  const raw =
    customApp && typeof customApp === 'object' && 'branding' in customApp
      ? (customApp as { branding?: unknown }).branding
      : null;
  const record =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : null;
  const settings =
    record?.['settings'] &&
    typeof record['settings'] === 'object' &&
    !Array.isArray(record['settings'])
      ? (record['settings'] as Record<string, unknown>)
      : null;
  const displayName =
    typeof settings?.['displayName'] === 'string' &&
    settings['displayName'].trim()
      ? settings['displayName'].trim()
      : 'Home';
  const logoUrl =
    typeof record?.['logoUrl'] === 'string' && record['logoUrl'].trim()
      ? record['logoUrl'].trim()
      : null;
  return { displayName, logoUrl };
}

function listNavigationEntries(
  customAppConfig: CustomAppConfig,
  showDraftRoutes: boolean,
  locale?: string
): NavigationEntry[] {
  const pagesById = new Map(customAppConfig.pages.map(page => [page.id, page]));
  const sorted = [...customAppConfig.navigation].sort(
    (a, b) => a.order - b.order
  );
  const byId = new Map(sorted.map(item => [item.id, item]));
  const byParent = new Map<string | null, typeof sorted>();
  for (const item of sorted) {
    const parentId =
      item.parentId && byId.has(item.parentId) ? item.parentId : null;
    const list = byParent.get(parentId) ?? [];
    list.push(item);
    byParent.set(parentId, list);
  }

  const result: NavigationEntry[] = [];
  const visited = new Set<string>();
  const pushBranch = (parentId: string | null, depth: number) => {
    for (const item of byParent.get(parentId) ?? []) {
      if (visited.has(item.id) || depth >= MAX_RENDER_DEPTH) continue;
      visited.add(item.id);
      const page = item.pageId ? pagesById.get(item.pageId) : null;
      if (page && (showDraftRoutes || page.status === 'published')) {
        result.push({
          id: item.id,
          label: resolveNavigationLabel(item, locale, page.id),
          href: page.path,
          depth,
        });
      } else if (!page && item.href) {
        result.push({
          id: item.id,
          label: resolveNavigationLabel(item, locale, item.href),
          href: item.href,
          depth,
        });
      }
      pushBranch(item.id, depth + 1);
    }
  };
  pushBranch(null, 0);
  return result;
}

function resolveNavigationLabel(
  item: CustomAppConfig['navigation'][number],
  locale: string | undefined,
  fallback: string
): string {
  const normalizedLocale = locale?.trim().toLowerCase();
  const baseLocale = normalizedLocale?.split(/[-_]/)[0];
  const localized =
    (normalizedLocale ? item.labelsI18n?.[normalizedLocale] : undefined) ??
    (baseLocale ? item.labelsI18n?.[baseLocale] : undefined);
  if (localized) return localized;
  return item.labelFormula?.type === 'value'
    ? String(item.labelFormula.value)
    : fallback;
}

export const CustomAppNavigationComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.CUSTOM_APP_NAVIGATION,
  type: 'navigation',
  name: 'Custom App Navigation',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    'Nav'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    visible: true,
    showDraftRoutes: true,
    orientation: 'auto',
  },
  render(props) {
    return createElement(CustomAppNavigationRenderer, props);
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      'Nav Custom App Navigation'
    );
  },
};
